import { useCallback, useMemo } from 'react'
import {
    isChannelStreamId,
    isDMChannelStreamId,
    isGDMChannelStreamId,
    spaceIdFromChannelId,
} from '@towns-protocol/sdk'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { uniq } from 'lodash-es'
import { notUndefined } from 'ui/utils/utils'
import {
    Channel,
    useChannelMembers,
    useChannelSlashCommands,
    useDMSlashCommands,
    useUserLookupArray,
} from 'use-towns-client'

import { convertUserToCombobox, getUserHashMap } from '../components/plate-ui/autocomplete/helpers'
import {
    AtChannelUser,
    TComboboxItemWithData,
    TSlashCommand,
    TUserIDNameMap,
    TUserWithChannel,
} from '../components/plate-ui/autocomplete/types'

const stableArray: string[] = []

export type TGetUserMentions = (
    searchTerm?: string,
    limit?: number,
) => TComboboxItemWithData<TUserWithChannel>[]

export const useEditorMemberData = (
    spaceMemberIds: string[],
    channelId: string,
    draftUserIds: string[] = stableArray,
) => {
    const { memberIds: channelMemberIds } = useChannelMembers()
    const isDraft = channelId === ''
    const isDMorGDM = useMemo(
        () => isDMChannelStreamId(channelId) || isGDMChannelStreamId(channelId),
        [channelId],
    )

    const availableMemberIds = useMemo(
        () => uniq([...draftUserIds, ...channelMemberIds, ...spaceMemberIds]),
        [channelMemberIds, draftUserIds, spaceMemberIds],
    )
    const availableMembers = useUserLookupArray(availableMemberIds, channelId)

    const getUserMentions = useCallback(
        (searchTerm?: string, limit: number = 50) => {
            // Start with special mentions for non-DM channels
            const specialMentions = isDMorGDM || isDraft ? [] : [AtChannelUser]

            // Filter and limit users based on search term
            let filteredMembers = [...specialMentions, ...availableMembers]
            if (searchTerm && searchTerm.length > 0) {
                const lowerSearch = searchTerm.toLowerCase()
                filteredMembers = filteredMembers.filter(
                    (user) =>
                        user.displayName?.toLowerCase().includes(lowerSearch) ||
                        user.username?.toLowerCase().includes(lowerSearch),
                )
            }

            // Only process the limited set
            const limitedMembers = filteredMembers.slice(0, limit)

            const result = limitedMembers
                .map((user) => convertUserToCombobox(user, [...draftUserIds, ...channelMemberIds]))
                .filter(notUndefined)

            return result
        },
        [isDMorGDM, isDraft, availableMembers, channelMemberIds, draftUserIds],
    )

    // Hash map of user's displayName and userId. This is used to easily lookup and
    // convert @name mention to userId while pasting and editing in O(1) time
    const userHashMap: TUserIDNameMap = useMemo(() => {
        return getUserHashMap(availableMembers)
    }, [availableMembers])

    return { getUserMentions, userHashMap }
}

export const useEditorChannelData = (channels: Channel[]) => {
    const channelMentions: TComboboxItemWithData<Channel>[] = useMemo(() => {
        return channels
            .map((channel) => ({
                text: channel.label,
                key: channel.id,
                data: channel,
            }))
            .filter(notUndefined)
    }, [channels])

    return { channelMentions }
}

export const useEditorSlashCommandData = (
    streamId: string | undefined,
    { enabled }: { enabled?: boolean } = {},
) => {
    const { enableBotDm } = useFeatureFlags()
    // Use channel slash commands for regular channels (spaces)
    // Only compute spaceId when not in a DM to avoid invalid address errors
    const channelId = streamId && isChannelStreamId(streamId) ? streamId : undefined
    const spaceId = channelId ? spaceIdFromChannelId(channelId) : undefined
    const dmId = streamId && isDMChannelStreamId(streamId) ? streamId : undefined

    const { data: channelSlashCommands, isLoading: channelIsLoading } = useChannelSlashCommands(
        spaceId,
        channelId,
        { enabled: enabled && !!spaceId && !!channelId },
    )

    // Use DM slash commands for 1:1 DMs
    const { data: dmSlashCommands, isLoading: dmIsLoading } = useDMSlashCommands(dmId, {
        enabled: enabled && !!dmId && enableBotDm,
    })
    // Select the appropriate data source
    const slashCommandsData = dmId ? dmSlashCommands : channelSlashCommands
    const isLoading = dmId ? dmIsLoading : channelIsLoading

    const slashCommands: TComboboxItemWithData<TSlashCommand>[] = useMemo(() => {
        if (!slashCommandsData) {
            return []
        }

        return slashCommandsData.flatMap((appData) => {
            return (appData.slashCommands || []).map((cmd) => ({
                text: cmd.name,
                key: `${appData.appAddress}-${cmd.name}`,
                data: {
                    name: cmd.name,
                    description: cmd.description || '',
                    appAddress: appData.appAddress,
                    appClientAddress: appData.appClientAddress,
                    botName: appData.botName,
                },
            }))
        })
    }, [slashCommandsData])

    return { slashCommands, isLoadingSlashCommands: isLoading }
}
